import { gql } from '@apollo/client'
import type { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import getApolloClient from 'src/apollo-client'
import Footer from 'src/components/Footer'
import NavBar from 'src/components/LandingNav'
import UserProfile from 'src/components/UserProfile'
import { PAGE_SIZE } from 'src/constants'
import {
  ALL_REVIEW_FIELDS,
  CARD_LOCATION_FIELDS,
  USER_FRAGMENT
} from 'src/constants/fragments'
import {
  ILocation,
  Interest,
  IQuery,
  IQueryLocationArgs,
  IUser,
  IUserListingsArgs,
  IUserReviewsArgs
} from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { ErrorCode, processRequestError } from 'src/utils/errors'
import { imgUrl } from 'src/utils/image'
import { isValidUUID } from 'src/utils/validateId'

const GooglePopup = dynamic(() => import('src/components/Login/GooglePopup'))

const GET_LOCATION = gql`
  ${USER_FRAGMENT}
  ${CARD_LOCATION_FIELDS}
  ${ALL_REVIEW_FIELDS}

  query GetLocation(
    $pagination: PaginationInput!
    $locationId: String!
    $interest: Interest!
    $business: BusinessInput!
    $searchInput: SearchLocationsInput
  ) {
    location(locationId: $locationId) {
      ...CardLocationFields

      bussinessProfile {
        name
        description
        profilePicture {
          url
        }
      }

      user {
        ...CoreUserFields

        listings(pagination: $pagination, business: $business) {
          total
          hits {
            ...CardLocationFields
          }
        }

        reviews(pagination: $pagination, interest: $interest) {
          total
          avgRating
          hits {
            ...AllReviewFields
          }
        }
      }
    }
  }
`

const { titleF, descriptionF } = {
  titleF: ({ firstname }: IUser, { shortAddress }: ILocation) =>
    firstname
      ? `SpotMe | ${firstname} ${shortAddress}`
      : 'SpotMe | Encuentra almacenamiento para tus cosas',
  descriptionF: ({ firstname }: IUser, { shortAddress }: ILocation) =>
    `${
      firstname
        ? `Espacios en renta ${firstname} ${shortAddress}.`
        : 'Soluciones de espacios para individuos, negocios y vehículos.'
    } Fácil, seguro y económico. ¡Vuélvete parte de la comunidad!`
}

type IProps = {
  user: IUser
  location: ILocation
}

const Page: NextPage<IProps> = ({ user, location }) => {
  const {
    name,
    profilePicture,
    description: desc
  } = location.bussinessProfile ?? {}

  const locations = user.listings?.hits?.filter(
    (l) => l?.bussinessProfile?.name === name
  )
  if (locations?.length === 0) locations.push(location)

  const bussinessUser: IUser = {
    ...user,
    firstname: name,
    lastname: '',
    description: desc,
    profilePicture: profilePicture,
    listings: locations?.length
      ? {
          total: locations?.length,
          hits: locations
        }
      : null
  }

  const title = titleF(bussinessUser, location)
  const description = descriptionF(bussinessUser, location)
  const img = bussinessUser.profilePicture?.url ?? '/images/cover.png'
  const image = imgUrl(img, { w: 1200, q: 50 })
  const url = `https://www.spotme.mx/business/${location.id}`

  return (
    <div>
      <NextSeo
        noindex
        title={title}
        description={description}
        canonical={url}
        openGraph={{
          type: 'website',
          url,
          title,
          description,
          images: [{ url: image, width: 1200, height: 630 }]
        }}
      />

      <main>
        <NavBar />
        <UserProfile
          user={bussinessUser}
          isBusiness
          locationId={location.id}
          configurationId={null}
        />
        <Footer />

        <GooglePopup />
      </main>
    </div>
  )
}

export default Page

export const getServerSideProps: GetServerSideProps<IProps | {}> = async (
  context
) => {
  const { locale, query } = context
  const { id } = query
  const locationId = id && typeof id === 'string' ? id : null

  if (!locationId || !isValidUUID(locationId)) {
    return { notFound: true }
  }

  const client = getApolloClient(context)
  let response
  try {
    response = await client.query<
      Pick<IQuery, 'location'>,
      IQueryLocationArgs & IUserListingsArgs & IUserReviewsArgs
    >({
      query: GET_LOCATION,
      variables: {
        locationId,
        pagination: { from: 0, pageSize: PAGE_SIZE },
        interest: Interest.Host,
        business: { locationId }
      }
    })
  } catch (error) {
    const { code } = processRequestError(error, 'Error retrieving location.')
    if (code === ErrorCode.BadUserInput) {
      return { notFound: true }
    }
    return {
      props: {},
      redirect: {
        destination: RedirectUrls.Home,
        permanent: false
      }
    }
  }

  const location = response.data?.location
  if (!location) return { notFound: true }
  const { user, urlRoute } = location
  if (!user || !location.bussinessProfile) {
    return {
      props: {},
      redirect: {
        destination: urlRoute ?? '',
        permanent: false
      }
    }
  }

  const translations = await serverSideTranslations(locale ?? 'es', [
    'common',
    'footer',
    'nav',
    'platform_stats',
    // `admin` intentionally NOT shipped here — admin-only, lazy-loaded by
    // UserProfile via useLazyNamespace. See docs/performance/large-page-data.md.
    'auth',
    'user'
  ])
  return {
    props: {
      ...translations,
      user,
      location: response.data?.location
    }
  }
}
